// Import React and necessary hooks
import React, { useState, useRef, useEffect } from "react";
// Import react-admin utilities
import { Record } from "react-admin";
// Import Material-UI components for layout and form inputs
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Tabs,
  Tab,
} from "@material-ui/core";
import { useAppData } from "../../../common/hooks/useAppData";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { useNotify } from "react-admin";
import { HookFormTabbedForm } from "../../common/form/hook-form/HookFormTabbedForm";
import { HookFormTab } from "../../common/form/hook-form/HookFormTab";
import { useFormContext, Controller } from "react-hook-form";

enum Vendor {
  Geometris = "Geometris",
  PacificTrack = "PacificTrack",
}

interface EldMultipleFormProps {
  record?: Record;
}

interface FileUploadResponse {
  processId?: string;
  fileData?: string[][];
}

export const EldMultipleForm = (props: EldMultipleFormProps) => {
  const [fileData, setFileData] = useState<string[][] | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadInfoId, setUploadInfoId] = useState<string>("");
  const [hasHeader, setHasHeader] = useState(false);
  const [serialNumberColumnIndex, setSerialNumberColumnIndex] = useState<
    number | undefined
  >(undefined);
  const [macAddressColumnIndex, setMacAddressColumnIndex] = useState<
    number | undefined
  >(undefined);
  const [uploadStatus, setUploadStatus] = useState<
    "none" | "uploading" | "uploaded" | "failed"
  >("none");

  const [nestedTab, setNestedTab] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const axiosInstance = useAxiosInstance();
  const notify = useNotify();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    register,
  } = useFormContext();

  const vendorTypeValue = watch("vendorType");

  useEffect(() => {
    register("serialNumberColumnIndex", {
      required: "Serial Number column is required",
      validate: (value) =>
        value !== "" && value !== undefined && value !== null,
    });
    register("macAddressColumnIndex", {
      validate: (value) => {
        if (vendorTypeValue === Vendor.PacificTrack) {
          return (
            (value !== "" && value !== undefined && value !== null) ||
            "MAC address column is required"
          );
        }
        return true;
      },
    });
  }, [register, vendorTypeValue]);

  const tenants = useAppData((x) => x.tenants) ?? [];

  const vendorChoices = [
    { id: Vendor.Geometris, name: "Geometris" },
    { id: Vendor.PacificTrack, name: "Pacific Track" },
  ];

  const handleFileUpload = async (file: File) => {
    if (!axiosInstance) {
      notify("Error: No API connection available", "error");
      return;
    }

    setIsUploading(true);
    setUploadStatus("uploading");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post<FileUploadResponse>(
        "/api/app/masterelddevice/uploadfile",
        formData
      );
      console.log({ response });
      // Save processId and fileData from response
      if (response.data.processId) {
        setUploadInfoId(response.data.processId);
        setValue("uploadInfoId", response.data.processId);
      }

      if (response.data.fileData) {
        setFileData(response.data.fileData);
        setUploadStatus("uploaded");
        notify("File uploaded successfully", "info");
      } else {
        setUploadStatus("failed");
        notify("File must be a valid CSV or Excel file", "error");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadStatus("failed");
      notify("File upload failed. Try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCheckboxChange = (index: number) => {
    if (!fileData || fileData.length === 0) return;

    const headerRow = fileData[0];
    if (index >= headerRow.length) return;

    setSerialNumberColumnIndex(index);
    setValue("serialNumberColumnIndex", index, { shouldValidate: true });
  };

  const handleMacCheckboxChange = (index: number) => {
    if (!fileData || fileData.length === 0) return;
    setMacAddressColumnIndex(index);
    setValue("macAddressColumnIndex", index, { shouldValidate: true });
  };

  const renderFileDataTable = (
    selectedColumnIndex: number | undefined,
    onColumnSelect: (index: number) => void
  ) => {
    if (!fileData || fileData.length === 0) return null;

    return (
      <Paper style={{ marginTop: 16, overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {fileData[0].map((header, index) => (
                <TableCell key={index}>
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={selectedColumnIndex === index}
                      onChange={() => onColumnSelect(index)}
                      size="small"
                    />
                    <Typography variant="body2">{header}</Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fileData.slice(1).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    style={{
                      backgroundColor:
                        selectedColumnIndex === cellIndex
                          ? "#f5f5f5"
                          : "transparent",
                    }}
                  >
                    <Typography variant="body2">{cell}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const isSerialNumberSelected =
    typeof serialNumberColumnIndex === "number" && serialNumberColumnIndex >= 0;
  const isMacAddressSelected =
    typeof macAddressColumnIndex === "number" && macAddressColumnIndex >= 0;

  let columnSelectionError = "";
  if (fileData && fileData.length > 0) {
    if (vendorTypeValue === Vendor.PacificTrack) {
      if (!isSerialNumberSelected && !isMacAddressSelected) {
        columnSelectionError =
          "Please select both Serial Number and MAC address columns.";
      } else if (!isSerialNumberSelected) {
        columnSelectionError = "Please select the Serial Number column.";
      } else if (!isMacAddressSelected) {
        columnSelectionError = "Please select the MAC address column.";
      }
    } else if (vendorTypeValue === Vendor.Geometris) {
      if (!isSerialNumberSelected) {
        columnSelectionError = "Please select the Serial Number column.";
      }
    }
  }

  return (
    <>
      <HookFormTabbedForm className="form-create-edit" {...props}>
        <HookFormTab label="General Details">
          <ProtectedElement source="vendorType">
            <Controller
              name="vendorType"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth margin="normal">
                  <InputLabel>Vendor *</InputLabel>
                  <Select label="Vendor *" {...field}>
                    {vendorChoices.map((choice) => (
                      <MenuItem key={choice.id} value={choice.id}>
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </ProtectedElement>

          <ProtectedElement source="assignedToTenantId">
            <Controller
              name="assignedToTenantId"
              control={control}
              render={({ field }) => (
                <FormControl
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  className="form-create-edit"
                >
                  <InputLabel>Assigned to tenant</InputLabel>
                  <Select {...field} label="Assigned to tenant" displayEmpty>
                    <MenuItem value="">
                      <em>No assigned tenant</em>
                    </MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id ?? ""}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </ProtectedElement>

          {vendorTypeValue === Vendor.Geometris && (
            <>
              <ProtectedElement source="debuggingEnabled">
                <Controller
                  name="debuggingEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value || false}
                          onChange={field.onChange}
                        />
                      }
                      label="Debugging enabled"
                    />
                  )}
                />
              </ProtectedElement>
              <ProtectedElement source="dataPlanEnabled">
                <Controller
                  name="dataPlanEnabled"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value !== false}
                          onChange={field.onChange}
                        />
                      }
                      label="Data plan enabled"
                    />
                  )}
                />
              </ProtectedElement>
            </>
          )}

          <ProtectedElement source="note">
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  multiline
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
              )}
            />
          </ProtectedElement>

          <Controller
            name="uploadInfoId"
            control={control}
            defaultValue={uploadInfoId}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Controller
            name="hasHeader"
            control={control}
            defaultValue={hasHeader}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          {/* Use register for serialNumberColumnIndex and macAddressColumnIndex */}
          <input
            type="hidden"
            {...register("serialNumberColumnIndex", {
              required: "Serial Number column is required",
              validate: (value) =>
                value !== "" && value !== undefined && value !== null,
            })}
            value={serialNumberColumnIndex?.toString() || ""}
            readOnly
          />
          <input
            type="hidden"
            {...register("macAddressColumnIndex", {
              validate: (value) => {
                if (vendorTypeValue === Vendor.PacificTrack) {
                  return (
                    (value !== "" && value !== undefined && value !== null) ||
                    "MAC address column is required"
                  );
                }
                return true;
              },
            })}
            value={macAddressColumnIndex?.toString() || ""}
            readOnly
          />
          {/* Show validation errors */}
          {errors.serialNumberColumnIndex && (
            <Typography variant="body2" color="error">
              {errors.serialNumberColumnIndex.message}
            </Typography>
          )}
          {errors.macAddressColumnIndex && (
            <Typography variant="body2" color="error">
              {errors.macAddressColumnIndex.message}
            </Typography>
          )}
          {/* Endpoint override to use the correct API endpoint */}
          <Controller
            name="__endpointOverride"
            control={control}
            defaultValue="/api/app/masterelddevice/multipleelddevices"
            render={({ field }) => <input type="hidden" {...field} />}
          />
        </HookFormTab>

        <HookFormTab label="Select ELD">
          <ProtectedElement source="eldFile">
            <Box position="relative">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                disabled={isUploading || !vendorTypeValue}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
                style={{ display: "none" }}
                ref={fileInputRef}
              />
              <Button
                variant="outlined"
                component="label"
                disabled={isUploading || !vendorTypeValue}
                fullWidth
                style={{ marginTop: 16, marginBottom: 16 }}
              >
                {isUploading ? "Uploading..." : "Choose ELD file"}
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                  disabled={isUploading || !vendorTypeValue}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      handleFileUpload(file);
                    }
                  }}
                />
              </Button>
              {!vendorTypeValue && (
                <Typography variant="body2" color="error">
                  Select a vendor before uploading a file.
                </Typography>
              )}
              {uploadStatus === "uploaded" && (
                <Typography variant="body2" color="primary">
                  File uploaded successfully
                </Typography>
              )}
              {uploadStatus === "failed" && (
                <Typography variant="body2" color="error">
                  File upload failed
                </Typography>
              )}
            </Box>
          </ProtectedElement>

          {fileData && fileData.length > 0 && (
            <>
              <ProtectedElement source="hasHeader">
                <Controller
                  name="hasHeader"
                  control={control}
                  defaultValue={hasHeader}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value || false}
                          onChange={(event) => {
                            field.onChange(event.target.checked);
                            setHasHeader(event.target.checked);
                          }}
                        />
                      }
                      label="Has header"
                    />
                  )}
                />
              </ProtectedElement>

              <Tabs
                value={nestedTab}
                onChange={(_, newValue) => setNestedTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginTop: 16 }}
              >
                <Tab label="Select S/N column" />
                {vendorTypeValue === Vendor.PacificTrack && (
                  <Tab label="Select MAC address column" />
                )}
              </Tabs>

              {nestedTab === 0 && (
                <Box mt={2}>
                  {renderFileDataTable(
                    serialNumberColumnIndex,
                    handleCheckboxChange
                  )}
                </Box>
              )}
              {nestedTab === 1 && vendorTypeValue === Vendor.PacificTrack && (
                <Box mt={2}>
                  {renderFileDataTable(
                    macAddressColumnIndex,
                    handleMacCheckboxChange
                  )}
                </Box>
              )}
            </>
          )}
          {columnSelectionError && (
            <Typography variant="body2" color="error">
              {columnSelectionError}
            </Typography>
          )}
        </HookFormTab>
      </HookFormTabbedForm>
    </>
  );
};
